import { useState } from "react";
import { useTranslation } from "react-i18next";

import { ActivityRow, useActivityFeed, type ActivityFilter as FilterValue } from "@/entities/activity";
import { ActivityFilter } from "@/features/activity-filter";
import { useNavigate } from "react-router";

import { ROUTES } from "@/shared/config";
import { staggerIndex } from "@/shared/lib";
import { Button } from "@/shared/ui";
import { ActivityHero } from "@/widgets/activity-hero";
import { ScreenSkeleton, ScreenState } from "@/widgets/screen-state";
import { TopBar } from "@/widgets/top-bar";

/**
 * Лента событий.
 *
 * Пустое состояние — не заглушка, а отдельный экран: робот в кольцах, таймлайн
 * и призраки будущих событий объясняют, ЧТО именно тут появится. Фильтры при
 * этом остаются на месте — пустой может оказаться и конкретная категория, а не
 * лента целиком.
 *
 * Фильтрует сервер: фильтр уходит в запрос, а не режет пришедшую страницу.
 */
export function ActivityFeedPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [filter, setFilter] = useState<FilterValue>("all");
  const { data, isPending, isError, refetch, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useActivityFeed(filter);

  const items = data?.pages.flatMap((page) => page.items) ?? [];

  return (
    <div className="flex flex-col gap-3 px-5 py-3">
      <TopBar title={t("screens.activity")} subtitle={t("activity.subtitle")} />

      <ActivityFilter value={filter} onChange={setFilter} />

      {isPending ? (
        <ScreenSkeleton blocks={[92, 92, 92]} />
      ) : isError ? (
        <ScreenState
          tone="loss"
          icon="warningFill"
          title={t("states.error.title")}
          body={t("states.error.body")}
          action={{ label: t("states.error.action"), onClick: () => void refetch() }}
        />
      ) : items.length === 0 ? (
        <section className="flex flex-col items-center">
          <ActivityHero />
          <h2 className="text-h2 font-bold text-text-bright">{t("activity.empty.title")}</h2>
          <p className="mt-3 max-w-[260px] text-center text-small font-normal text-text-secondary">
            {t("activity.empty.body")}
          </p>
          <Button
            size="pill"
            className="mt-5"
            onClick={() => void navigate(ROUTES.robots)}
          >
            {t("activity.empty.action")}
          </Button>
        </section>
      ) : (
        <>
          <ul className="flex flex-col gap-3">
            {items.map((item, index) => (
              <li key={item.id} className="stagger" style={staggerIndex(index)}>
                <ActivityRow item={item} />
              </li>
            ))}
          </ul>

          {hasNextPage ? (
            <Button
              size="cta"
              variant="ghost"
              block
              disabled={isFetchingNextPage}
              onClick={() => void fetchNextPage()}
            >
              {t("common.loadMore")}
            </Button>
          ) : null}
        </>
      )}
    </div>
  );
}
